import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  countryService,
  GetCountriesParams,
  CreateCountryRequest,
  UpdateCountryRequest,
} from "../api/services/countries/countryService";

const COUNTRIES_STALE_TIME =
  Number(process.env.REACT_APP_COUNTRIES_STALE_TIME) || 5 * 60 * 1000;

// Query keys
export const countryKeys = {
  all: ["countries"] as const,
  lists: () => [...countryKeys.all, "list"] as const,
  list: (params: GetCountriesParams) =>
    [...countryKeys.lists(), params] as const,
  details: () => [...countryKeys.all, "detail"] as const,
  detail: (id: string) => [...countryKeys.details(), id] as const,
};

// Hook for getting countries with pagination and filters
export const useCountries = (params: GetCountriesParams = {}) => {
  return useQuery({
    queryKey: countryKeys.list(params),
    queryFn: () => countryService.getCountries(params),
    staleTime: COUNTRIES_STALE_TIME,
  });
};

// Hook for getting a single country
export const useCountry = (id: string) => {
  return useQuery({
    queryKey: countryKeys.detail(id),
    queryFn: () => countryService.getCountry(id),
    enabled: !!id,
    staleTime: COUNTRIES_STALE_TIME,
  });
};

// Hook for creating a country
export const useCreateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCountryRequest) =>
      countryService.createCountry(data),
    onSuccess: () => {
      // Invalidate and refetch countries list
      queryClient.invalidateQueries({ queryKey: countryKeys.lists() });
    },
  });
};

// Hook for updating a country
export const useUpdateCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      countryId,
      data,
    }: {
      countryId: string;
      data: UpdateCountryRequest;
    }) => countryService.updateCountry(countryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
    },
  });
};

// Hook for deleting a country
export const useDeleteCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => countryService.deleteCountry(id),
    onSuccess: (_, deletedId) => {
      // Remove the country from cache
      queryClient.removeQueries({ queryKey: countryKeys.detail(deletedId) });
      // Invalidate countries list
      queryClient.invalidateQueries({ queryKey: countryKeys.lists() });
    },
  });
};

// Custom hook for country management with common operations
export const useCountryManagement = (
  initialParams: GetCountriesParams = {},
) => {
  const queryClient = useQueryClient();

  const countriesQuery = useCountries(initialParams);
  const createMutation = useCreateCountry();
  const updateMutation = useUpdateCountry();
  const deleteMutation = useDeleteCountry();

  const refreshCountries = () => {
    queryClient.invalidateQueries({ queryKey: countryKeys.lists() });
  };

  const clearCache = () => {
    queryClient.clear();
  };

  return {
    // Queries
    countries: countriesQuery.data?.countries || [],
    isLoading: countriesQuery.isLoading,
    isError: countriesQuery.isError,
    error: countriesQuery.error,

    // Mutations
    createCountry: createMutation.mutate,
    updateCountry: updateMutation.mutate,
    deleteCountry: deleteMutation.mutate,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    // Utility functions
    refreshCountries,
    clearCache,
  };
};
